import React, { useEffect, useState } from "react";

import { Card, useMediaQuery } from "@material-ui/core";

import {
  BooleanField,
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
} from "react-admin";

import { ChevronRightIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { Pagination } from "../../../components/Pagination";
import { SearchField } from "../../../components/common/SearchField";
import { useSearchableList } from "../../../dataProvider/useSearchableList";

const CommoditySearchField = ({ label, alwaysOn, setDefaultFilter }) => {
  // using on-page search instead of server-side search
  // single page search filter only.
  const handleSearch = (value) => {
    if (!value) {
      setDefaultFilter({ active: "true" });
      return;
    }

    setDefaultFilter({
      active: "true",
      filter: {
        or: [
          { name: { contains: value } },
          { prompt: { contains: value } },
          { defaultUnits: { contains: value } },
          { locationLoadType: { contains: value } },
        ],
      },
    });
  };

  return (
    <SearchField
      id="commoditySearchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (setDefaultFilter) => [
  <CommoditySearchField
    key="commodity-search"
    label="Search"
    alwaysOn
    setDefaultFilter={setDefaultFilter}
  />,
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <RefreshButton />
  </TopToolbar>
);

export const CommodityList = (props) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const defaultSort = {
    field: `{"key":"commoditiesByActiveBySortKey", "search":"sortKey"}`,
    order: "ASC",
  };

  const [defaultFilter, setDefaultFilter] = useState({ active: "true" });

  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 25,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Load Types",
    defaultFilter,
    defaultSort,
    defaultPerPage,
    resource: "commoditys",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext.perPage, props.resource]);

  // Keep this pattern consistent with other searchable lists: clear stale filters on reset.
  useEffect(() => {
    if (listContext?.filter) {
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext.filter, defaultSort.field]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Load Types" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(setDefaultFilter)}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card>
        {xsDown ? (
          <SimpleList
            primaryText={(record) => record.name}
            secondaryText={(record) => `Default Units: ${record.defaultUnits}`}
            tertiaryText={(record) => `Load Type: ${record.locationLoadType}`}
            linkType="edit"
            rightIcon={() => (
              <ChevronRightIcon
                fontSize="large"
                color="secondary"
                style={{ marginLeft: 40, marginTop: 10 }}
              />
            )}
          />
        ) : (
          <Datagrid rowClick="edit">
            <TextField
              source="sortKey"
              label="Sort Key"
              sortable
              sortBy={`{"key":"commoditiesByActiveBySortKey", "search":"sortKey"}`}
            />
            <TextField
              source="name"
              label="Name"
              sortable
              sortBy={`{"key":"commoditiesByActiveByName", "search":"name"}`}
            />
            <TextField source="active" label="Active" sortable={false} />
            <TextField
              source="defaultUnits"
              label="Default Units"
              sortable={false}
            />
            <TextField
              source="locationLoadType"
              label="Load Type"
              sortable={false}
            />
            <BooleanField
              source="enableCounter"
              label="Pre-Count"
              sortable={false}
            />
            <FunctionField
              source="prompt"
              label="Prompt"
              render={(record) =>
                record.prompt?.length?.toLocaleString() || "-"
              }
              sortable={false}
            />
            <EditButton />
          </Datagrid>
        )}
        <Pagination {...listContext} rowsPerPageOptions={[25, 50, 100]} />
      </Card>
    </ListContextProvider>
  );
};
